import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    StepConnector,
    IconButton,
    Paper,
    Divider,
    alpha,
    styled
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PndRestApi from "src/utils/pnd/PndRestApi";
import { showNotification } from "src/shared/components/Notification";
import { ERROR_MESSAGES } from "src/utils/pnd/PndConstants";
import moment from 'moment';

const QontoConnector = styled(StepConnector)(({ theme }) => ({
    [`&.MuiStepConnector-alternativeLabel`]: {
        top: 10,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
    },
    [`&.MuiStepConnector-active`]: {
        [`& .MuiStepConnector-line`]: {
            borderColor: '#11998e',
        },
    },
    [`&.MuiStepConnector-completed`]: {
        [`& .MuiStepConnector-line`]: {
            borderColor: '#11998e',
        },
    },
    [`& .MuiStepConnector-line`]: {
        borderColor: '#eaeaf0',
        borderTopWidth: 3,
        borderRadius: 1,
    },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
    color: '#eaeaf0',
    display: 'flex',
    height: 22,
    alignItems: 'center',
    ...(ownerState.active && {
        color: '#11998e',
    }),
    '& .QontoStepIcon-completedIcon': {
        color: '#11998e',
        zIndex: 1,
        fontSize: 18,
    },
    '& .QontoStepIcon-circle': {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
    },
}));

function QontoStepIcon(props) {
    const { active, completed, className } = props;

    return (
        <QontoStepIconRoot ownerState={{ active }} className={className}>
            {completed ? (
                <CheckCircleIcon className="QontoStepIcon-completedIcon" />
            ) : (
                <div className="QontoStepIcon-circle" />
            )}
        </QontoStepIconRoot>
    );
}

const OrderTrackingModal = ({ modalShow, setModalShow, isOrderId, isOrderNo }) => {
    const [trackingData, setTrackingData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (modalShow) {
            getStatusTrackingData();
        }
    }, [modalShow, isOrderId]);

    const handleClose = () => {
        setModalShow(false);
    }

    const getStatusTrackingData = async () => {
        setLoading(true);
        try {
            const response = await PndRestApi.GetOrderProcessedStatus(isOrderId);
            if (response.status === 200 && response.data.status === 200) {
                setTrackingData(response.data.data);
            } else if (response?.data?.status !== 204) {
                showNotification(
                    "theme-light",
                    "ltr",
                    `Error`,
                    response?.data?.message || "Failed to fetch tracking data",
                );
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification("theme-light", "ltr", "Error", ERROR_MESSAGES.tryCatchError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={modalShow}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '12px', overflow: 'hidden' }
            }}
        >
            <DialogTitle sx={{
                m: 0,
                p: 2,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocalShippingIcon sx={{ mr: 1.5 }} />
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                        Current Order Status
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ color: 'white' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
                <Paper elevation={0} sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: '12px',
                    border: '1px solid #eef2f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'white'
                }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                            Order Number
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#11998e' }}>
                            {isOrderNo}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                            Latest Status
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#32325d' }}>
                            {trackingData[trackingData.length - 1]?.actionDescription || 'N/A'}
                        </Typography>
                    </Box>
                </Paper>

                <Box sx={{ px: 2 }}>
                    <Stepper
                        activeStep={trackingData.length}
                        alternativeLabel
                        connector={<QontoConnector />}
                    >
                        {trackingData.map((step, index) => (
                            <Step key={index}>
                                <StepLabel StepIconComponent={QontoStepIcon}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: index === trackingData.length - 1 ? 700 : 500,
                                        color: index === trackingData.length - 1 ? '#11998e' : 'text.secondary',
                                        fontSize: '0.8rem',
                                        mt: 0.5
                                    }}>
                                        {step.actionDescription}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                                        {step.createdOn ? moment(step.createdOn).format('MMM DD, hh:mm A') : ''}
                                    </Typography>
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                        <AssignmentIcon sx={{ fontSize: '1.2rem', mr: 1, color: '#11998e' }} />
                        Tracking History
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {[...trackingData].reverse().map((item, index) => (
                            <Paper key={index} elevation={0} sx={{
                                p: 2,
                                borderRadius: '8px',
                                border: '1px solid #eef2f6',
                                borderLeft: `4px solid ${index === 0 ? '#11998e' : '#cbd5e0'}`,
                                bgcolor: index === 0 ? alpha('#11998e', 0.02) : 'white'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: index === 0 ? 700 : 600, color: index === 0 ? '#11998e' : '#2d3748' }}>
                                        {item.actionDescription}
                                    </Typography>
                                    {/* <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                        {item.processedTime ? moment(item.processedTime).format('MMMM DD, YYYY [at] hh:mm A') : 'N/A'}
                                    </Typography> */}
                                </Box>
                                {item.orderStatusId === 18 && item.hubUserName && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                        Processed by: {item.hubUserName}
                                    </Typography>
                                )}
                                {item.processedRiderName && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                        Processed Rider: {item.processedRiderName}
                                    </Typography>
                                )}
                                {item.processedTime && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                        Processed Time: {moment.utc(item.processedTime).local().format('MMMM DD, YYYY [at] hh:mm A')}
                                    </Typography>
                                )}
                                {item.remarks && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                                        Notes: {item.remarks}
                                    </Typography>
                                )}
                            </Paper>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

OrderTrackingModal.propTypes = {
    modalShow: PropTypes.bool.isRequired,
    setModalShow: PropTypes.func.isRequired,
    isOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isOrderNo: PropTypes.string
};

export default OrderTrackingModal;
